<template>
    <div
        :class="!previewMode ? 'sticky top-0 z-[var(--index-navbar)]' : 'relative'"
        class="app-top-navigation-bar"
        data-testid="top-bar-container"
    >
        <template v-if="tabbedView">
            <div
                v-if="showTabs"
                class="app-top-navigation-bar__container"
                data-testid="top-bar-inner"
            >
                <div class="top-bar-tabs" data-testid="top-bar-tabs-container">
                    <div class="top-bar-tabs__block">
                        <TopNavigationTabs
                            ref="topNavigationTabsRef"
                            data-testid="top-bar-tabs"
                            :preview-mode="previewMode"
                            :active-tab="activeTab"
                            :edit-mode="editMode"
                            :widget-group-list="widgetGroupList"
                            @reorder-groups="onReorderTabs"
                        />
                    </div>
                </div>
                <div
                    v-if="isDesktop && widgetGroupList.length"
                    class="top-bar-actions py-2"
                    data-testid="top-bar-actions"
                >
                    <div
                        v-if="editMode"
                        class="flex items-center gap-3"
                        data-testid="top-bar-edit-mode-actions"
                    >
                        <RedesignBaseButton
                            data-testid="top-bar-button-cancel"
                            size="small"
                            type="outline"
                            color="primary"
                            @click="onCancel"
                        >
                            {{ $t('common.cancel') }}
                        </RedesignBaseButton>

                        <RedesignBaseButton
                            data-testid="top-bar-button-apply"
                            size="small"
                            color="primary"
                            @click="onSaveTab"
                        >
                            {{ $t('common.apply') }}
                        </RedesignBaseButton>
                    </div>
                    <div
                        v-if="!editMode"
                        class="flex items-center gap-2 text-main-sm"
                    >
                        <RedesignBaseButton
                            data-testid="top-bar-button-edit-mode"
                            icon="Pencil"
                            size="small"
                            type="outline"
                            color="primary"
                            @click="toEditMode"
                        >
                            {{ $t('general.editMode') }}
                        </RedesignBaseButton>
                    </div>
                    <RedesignBaseButton
                        data-testid="top-bar-button-add-widget"
                        icon="PlusCircle"
                        size="small"
                        @click="clickToAddNewWidget"
                    >
                        {{ $t('dashboard.addNewWidgets') }}
                    </RedesignBaseButton>
                </div>
            </div>
            <div class="absolute -bottom-3 flex justify-center w-full app-top-navigation-bar__action" data-testid="top-bar-toggle-container">
                <div
                    class="w-22 bg-gray-400 hover:text-primary h-3 rounded-b-2xl text-white cursor-pointer flex items-center justify-center"
                    data-testid="top-bar-button-toggle"
                    @click="toggleSidebarState"
                >
                    <RedesignBaseIcon
                        name="ChevronDown"
                        :class="{ 'rotate-180': showTabs}"
                    />
                </div>
            </div>
        </template>
        <div
            v-else
            class="app-top-navigation-bar__container"
        >
            <div
                class="py-2 flex items-center justify-end px-1 lg:px-6 xl:px-8 w-full"
            >
                <RedesignNewGroupPopover
                    @create-from-template="openTemplatesModal"
                    data-testid="button-add-group"
                    @create-empty-group="onAddNewGroup"
                />
            </div>
        </div>
        <TemplatesGroupCreateModal
            ref="layoutsGroupCreateModalRef"
            :groups-count="props.widgetGroupList?.length || 0"
            @save="onCreateGroupFromTemplate"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import { ExtendedWidgetGroupList, WidgetGroupList, WidgetGroupListCreate } from '@/types/dashboard'
import TopNavigationTabs from '@/components/RedesignComponents/Tabs/TopNavigationTabs.vue'
import { useGroupNavigationProvide } from '@/composables/useGroupNavigationTabs'
import { isDesktop } from '@/composables/useBreakpoints'
import RedesignNewGroupPopover from '@/components/RedesignComponents/App/RedesignNewGroupPopover.vue'
import TemplatesGroupCreateModal from '@/components/RedesignComponents/Modal/TemplatesGroupCreateModal.vue'

interface IProps {
    previewMode?: boolean
    tabbedView?: boolean
    activeTab?: string | number
    editMode?: boolean
    fullScreen?: boolean
    widgetGroupList?: Array<WidgetGroupList>
}

const props = withDefaults(
    defineProps<IProps>(),
    {
        tabbedView: true,
        activeTab: '',
        editMode: false,
        previewMode: false,
        fullScreen: false,
        widgetGroupList: () => []
    }
)
const emit = defineEmits<{
    (e: 'update:editMode', pl: boolean): void
    (e: 'switch-tab', groupId: number): void,
    (e: 'save-dashboard'): void,
    (e: 'on-edit-widget-group'): void,
    (e: 'on-copy-widget-group', pl: WidgetGroupList): void,
    (e: 'on-remove-widget-group', pl: WidgetGroupList): void,
    (e: 'add-new-group', pl?: WidgetGroupListCreate): void,
    (e: 'add-new-widget', pl?: WidgetGroupList): void,
    (e: 'update:title', pl: {
        title: string,
        widgetsGroupID: string | number
    }): void,
    (e: 'reorder-groups', pl: Array<ExtendedWidgetGroupList>): void
}>()

/* Data */
const showTabs = ref(true)
const topNavigationTabsRef = ref()
const layoutsGroupCreateModalRef = ref<InstanceType<typeof TemplatesGroupCreateModal>>()

/* Methods */
function openTemplatesModal () {
    layoutsGroupCreateModalRef.value?.open()
}

function onCreateGroupFromTemplate (data: WidgetGroupListCreate) {
    onAddNewGroup(data)
    layoutsGroupCreateModalRef.value?.close()
}

function toggleSidebarState () {
    showTabs.value = !showTabs.value
}

function onChangeEditMode (val: boolean) {
    emit('update:editMode', val)
}

function onSwitchTab (tab: WidgetGroupList) {
    emit('switch-tab', tab.WidgetGroupID)
}

function onSaveTab () {
    emit('save-dashboard')
}

function clickToAddNewWidget () {
    const widgetGroupToEnableEdit = props.widgetGroupList.find(widgetGroup => String(widgetGroup.WidgetGroupID) === String(props.activeTab))
    emit('add-new-widget', widgetGroupToEnableEdit)
}

function onCopyGroup (group: WidgetGroupList) {
    emit('on-copy-widget-group', group)
}

function onRemoveGroup (group: WidgetGroupList) {
    emit('on-remove-widget-group', group)
}

function onAddNewGroup (data?: WidgetGroupListCreate) {
    emit('add-new-group', data)
}

function onCancel () {
    onChangeEditMode(false)
}

function onReorderTabs (payload: Array<ExtendedWidgetGroupList>) {
    emit('reorder-groups', payload)
}

function onUpdateGroupName (newName: string, widgetsGroupID: string | number) {
    emit('update:title', {
        title: newName,
        widgetsGroupID
    })
}

function toEditMode () {
    onChangeEditMode(true)
}

function scrollToActiveTab () {
    topNavigationTabsRef.value?.scrollToActiveTab()
}

/* Provide */
useGroupNavigationProvide({
    editMode: computed(() => props.editMode),
    onSwitchTab,
    onCopyGroup,
    onRemoveGroup,
    onAddNewGroup,
    onUpdateGroupName
})

defineExpose({
    scrollToActiveTab
})
</script>

<style lang="scss">
.app-top-navigation-bar {
    &__container {
        @apply overflow-hidden opacity-100 flex justify-between;
        direction: ltr;
        //min-height: 52px;
        box-shadow: 0 0 1px 0 var(--ui-lines), 0 1px 4px 0 var(--ui-lines);
        background-color: var(--secondaryBgColor-color);
    }

    .top-bar-tabs {
        @apply flex-grow relative overflow-hidden;
    }

    .top-bar-tabs__block {
        @apply flex flex-grow h-full;
    }

    .top-bar-actions {
        @apply flex items-center justify-end px-3 lg:px-8 xl:px-10 gap-3 xl:gap-4 flex-shrink-0;
    }

    ::-webkit-scrollbar-track:horizontal, ::-webkit-scrollbar {
        width: 4px;
        height: 8px;
        position: absolute;
    }
}
</style>
